var GiftWithPurchase = class extends HTMLElement {
  constructor() {
    super();
    this.gifts = JSON.parse(this.querySelector('[data-gifts-json]').innerHTML)?.filter(gift => gift.available)
    this.cartTotal = 0
    this.idsInCart = []
    this._onCartChangedListener = this._onCartChanged.bind(this)
    this.getCart()
  }

  async getCart () {
    const cartJson = await (await fetch(`${Shopify.routes.root}cart.js`)).json()
    this.cartData = cartJson
    this.cartItems = cartJson.items

    this.updateCartVariables()
    this.checkGifts()
    this.removeStaleGifts()
  }

  async connectedCallback() {
    this.threshold = Math.round(this.threshold * (Shopify.currency.rate || 1));
    document.addEventListener("cart:change", this._onCartChangedListener);
    // Listen for the explicit removal of any gift with purchase.
    document.addEventListener('click', this.removeAndIgnoreGiftListener);
  }

  disconnectedCallback() {
    document.removeEventListener("cart:change", this._onCartChangedListener);
    document.removeEventListener('click', this.removeAndIgnoreGiftListener);
  }

  /**
   * Handles the event of a gift being removed from the cart.
   * @param {Event} event
   */
  removeAndIgnoreGiftListener = (event) => {
    const linkTarget = event.target.closest('.line-item__remove');
    if (!linkTarget) {
      return;
    }

    const giftVariantId = linkTarget.getAttribute('data-gift-variant-id');
    if (!giftVariantId || !event.target.closest('line-item-quantity')) {
      // Not a gift being removed.
      return;
    }

    this.ignoreGift(giftVariantId);
  }

  /**
   * Marks a gift as ignored by storing its identifier in session storage.
   * @param {string} variantId - The gift's variant identifier to be ignored.
   */
  ignoreGift = (variantId) => {
    sessionStorage.setItem(`gwp-${variantId}`, ''); // No value needed.
  }

  /**
   * Checks if a gift is marked as ignored in session storage.
   * @param {string} variantId - The gift's variant identifier to check.
   * @returns {boolean} - Returns `true` if the gfit is ignored, otherwise `false`.
   */
  giftIsIgnored = (variantId) => {
    return sessionStorage.getItem(`gwp-${variantId}`) !== null;
  }

  /**
   * Removes the ignored status of a gift by removing it from session storage.
   * @param {string} variantId - The gift's variant identifier to allow.
   */
  allowGift = (variantId) => {
    sessionStorage.removeItem(`gwp-${variantId}`);
  }

  _onCartChanged(event) {
    this.cartItems = event.detail.cart.items
    this.updateCartVariables()
    this.checkGifts(event.detail.baseEvent);
    this.removeStaleGifts()
  }

  /**
   * Manages the gift variants in the cart based on promo requirements and specific cart events.
   * @async
   * @param {string} eventType - The type of event triggering the gift check, e.g. `variant:add`.
   */
  async checkGifts(eventType) {
    let reload = false
    let modify = false
    const atcData = { items: [] }
    const changeData = { updates: {} }
    // A gift stops being ignored if its qualifying product is attached with one of the following events.
    const targetEventTypes = ['variant:add'];

    const giftsByVariantId = new Map();

    this.gifts.forEach(gift => {
      const variantId = gift.id;
      const hasValidRequirements = this.checkGiftRequirementMet(gift);

      if (!giftsByVariantId.has(variantId)) {
        giftsByVariantId.set(variantId, {
          variant_id: variantId,
          type: gift.type,
          requirement: gift.requirement,
          promotions: [],
          has_any_valid_requirements: false,
          has_new_subscriber_promotion: false
        });
      }

      const giftGroup = giftsByVariantId.get(variantId);
      giftGroup.promotions.push({
        type: gift.type,
        requirement: gift.requirement,
        has_valid_requirements: hasValidRequirements
      });

      if (hasValidRequirements) {
        giftGroup.has_any_valid_requirements = true;
      }

      if (gift.type === 'new_subscriber') {
        giftGroup.has_new_subscriber_promotion = true;
        if (hasValidRequirements) {
          giftGroup.type = 'new_subscriber';
        }
      }
    });

    const giftVariants = Array.from(giftsByVariantId.values());

    // Lookahead to see whether a `new_subscriber` gift should be added.
    const newSubscriberGift = this.gifts.find(gift =>
      gift.type === 'new_subscriber' && this.checkGiftRequirementMet(gift)
    );

    for (const giftVariant of giftVariants) {
      if (giftVariant.has_any_valid_requirements && targetEventTypes.includes(eventType)) {
        // Adding a qualifying product to your cart will always allow the gift.
        this.allowGift(giftVariant.variant_id);
      }

      if (this.giftIsIgnored(giftVariant.variant_id)) {
        continue;
      }

      const giftInCart = this.getGiftInCart(giftVariant.variant_id);

      // Skip other gifts if a `new_subscriber` gift is valid.
      if (newSubscriberGift && !giftVariant.has_new_subscriber_promotion) {
        // Remove a previous gift because the `new_subscriber` gift takes priority.
        if (giftInCart) {
          changeData.updates[giftInCart.key] = 0;
          modify = true;
        }
        continue;
      }

      // Process the current gift based on its validity and presence in the cart.
      if (giftVariant.has_any_valid_requirements && !giftInCart) {
        atcData.items.push({
          id: giftVariant.variant_id,
          quantity: 1,
          properties: {
            '_gift-with-purchase': true
          },
        });
      } else if (!giftVariant.has_any_valid_requirements && giftInCart) {
        changeData.updates[giftInCart.key] = 0;
        modify = true;
      } else if (giftVariant.has_any_valid_requirements && giftInCart && giftInCart.quantity > 1) {
        changeData.updates[giftInCart.key] = 1;
        modify = true;
      }
    }

    // Add new gifts to the cart.
    if (atcData.items.length) {
      await this.addToCart(atcData);
      reload = true;
    }

    // Modify the cart to remove stale or conflicting gifts.
    if (modify) {
      await this.modifyGiftInCart(changeData);
      reload = true;
    }

    if (window.themeVariables.settings.pageType !== 'cart' || !reload) {
      return;
    }

    window.location.reload();
  }

  // Remove GWP items when associated GWP metaobject is deleted / disabled
  async removeStaleGifts() {
    const changeData = { updates: {} }
    for (const item of this.cartItems) {
      if (Object.hasOwn(item.properties, '_gift-with-purchase') && !this.gifts.some(gift => gift.id === item.variant_id)) {
        changeData.updates[item.key] = 0
      }
    }

    if (Object.keys(changeData.updates).length) {
      await fetch(`${Shopify.routes.root}cart/update.js`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(changeData)
      })
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true
      }))
    }
  }

  checkGiftRequirementMet (gift) {
    if (gift.type === 'Cart Total') {
      return this.cartTotal >= gift.requirement

    } else if (gift.type === 'Item in Cart') {
      return gift.requirement.split(',').some(id => this.idsInCart.includes(Number(id)))

    } else if (gift.type === 'new_subscriber') {
      // If the user is logged in and tagged as such.
      const isSubscriber = gift.requirement.includes('ordergroove-subscriber') ||
        gift.requirement.includes('Active Ordergroove Subscriber');
      // If the cart has any subscription products.
      const cartQualifies = this.cartItems.some((item) => item.selling_plan_allocation);
      return !isSubscriber && cartQualifies;

    } else if (!gift.type) {
      return false
    }

    return false
  }

  async addToCart (data) {
    const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
      body: JSON.stringify(data),
      method: "POST",
      headers: {
        'Content-Type': 'application/json',
        "X-Requested-With": "XMLHttpRequest"
      }
    })
    const responseJson = await response.json()
    if (response.ok) {
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true
      }))
    } else {
      this.dispatchEvent(new CustomEvent("cart:error", {
        bubbles: true,
        detail: {
          error: responseJson["description"]
        }
      }));
    }
  }

  async modifyGiftInCart (data) {
    const response = await fetch(`${Shopify.routes.root}cart/update.js`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(data)
    })
    if (response.ok) {
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true
      }))
    }
  }

  getGiftInCart (giftVariantId) {
    return this.cartItems.find(item => item.id === giftVariantId && Object.hasOwn(item.properties, '_gift-with-purchase'))
  }

  updateCartVariables () {
    this.cartTotal = this.cartItems.reduce((total, item) => {
      if (Object.hasOwn(item.properties, '_gift-with-purchase')) return total
      return total + item.final_line_price
    }, 0)

    this.idsInCart = this.cartItems.filter((item) => item.properties['_gift-with-purchase'] !== true).map(item => item.id)
  }
};
if (!window.customElements.get('gift-with-purchase')) {
  window.customElements.define('gift-with-purchase', GiftWithPurchase);
}

var LineItemVariantSelector = class extends HTMLElement {
  constructor() {
    super();
    this.select = this.querySelector('select')
    this.key = this.dataset.key
    this.value = this.select.value
    this.getCart()
  }

  async getCart() {
    const cartJson = await (await fetch(`${Shopify.routes.root}cart.js`)).json()
    this.cartItems = cartJson.items
    this.init()
  }

  init () {
    this.cartItem = this.cartItems.find(item => item.key === this.key)
    this.select.addEventListener('change', this.changeVariant.bind(this))
  }

  async changeVariant () {
    if (this.select.value === this.value) { return }
    const changeData = {
      id: this.cartItem.key,
      quantity: 0
    }

    const addData = {
      items: [
        {
          id: this.select.value,
          quantity: this.cartItem.quantity,
          ...(this.cartItem.selling_plan_allocation?.selling_plan?.id) && { selling_plan: this.cartItem.selling_plan_allocation.selling_plan.id }
        }
      ]
    }

    // Remove Old Item
    await fetch(`${Shopify.routes.root}cart/change.js`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(changeData)
    })

    // Add New Item
    const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
      body: JSON.stringify(addData),
      method: "POST",
      headers: {
        'Content-Type': 'application/json',
        "X-Requested-With": "XMLHttpRequest"
      }
    })
    const responseJson = await response.json()
    if (response.ok) {
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true
      }))
    } else {
      this.dispatchEvent(new CustomEvent("cart:error", {
        bubbles: true,
        detail: {
          error: responseJson["description"]
        }
      }));
    }

    if (window.themeVariables.settings.pageType !== "cart") { return }
    window.location.reload()
  }
};
if (!window.customElements.get('line-item-variant-selector')) {
  window.customElements.define('line-item-variant-selector', LineItemVariantSelector);
}


var LineItemSellingPlanSelector = class extends HTMLElement {
  constructor() {
    super();
    this.selectWrapper = this.querySelector('.line-item__select-wrapper');
    this.select = this.querySelector('select');
    this.checkbox = this.querySelector('.subscribe-toggle');
    this.key = this.dataset.key;
    this.cartItem = null;

    // Event bindings
    if (this.checkbox) {
      this.checkbox.addEventListener('change', this._debounce(this.onToggleSubscribe, 300));
    }
    if (this.select) {
      this.select.addEventListener('change', this.onChangePlan.bind(this));
    }

    // Load cart, then init with the actual item state
    this.getCart();
  }

  /**
   * Debounce helper function. Delays invoking a function until after `delay`
   * milliseconds have passed since the last time it was invoked.
   * @param {Function} func The function to debounce.
   * @param {number} delay The number of milliseconds to delay.
   * @returns {Function} The new debounced function.
   */
  _debounce(func, delay) {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func.apply(this, args);
      }, delay);
    };
  }

  async getCart() {
    const res = await fetch(`${Shopify.routes.root}cart.js`);
    const cartJson = await res.json();
    this.cartItems = cartJson.items || [];
    this.cartItem = this.cartItems.find(item => item.key === this.key);

    // Sync UI to current state (subscribed vs one-time)
    const isSubscribed = !!(this.cartItem && this.cartItem.selling_plan_allocation);
    this.setSelectVisibility(isSubscribed);

    if (this.select && isSubscribed && this.cartItem.selling_plan_allocation) {
      const currentId = this.cartItem.selling_plan_allocation.selling_plan.id;
      if (String(this.select.value) !== String(currentId)) {
        this.select.value = String(currentId);
      }
    }
  }

  setSelectVisibility(show) {
    if (!this.selectWrapper) {
      return;
    }

    if (show) {
      this.selectWrapper.removeAttribute('hidden');
      if (this.checkbox) {
        this.checkbox.setAttribute('aria-expanded', 'true');
      }
    } else {
      this.selectWrapper.setAttribute('hidden', '');
      if (this.checkbox) {
        this.checkbox.setAttribute('aria-expanded', 'false');
      }
    }
  }

  async onToggleSubscribe() {
    const wantsSubscribe = this.checkbox.checked;

    // Toggle the select UI
    this.setSelectVisibility(wantsSubscribe);

    // Build change payload: subscribe => set plan id; one-time => null
    const sellingPlanValue = wantsSubscribe
      ? Number(this.select.value)
      : null;

    await this.pushChange({
      id: this.key,
      selling_plan: sellingPlanValue,
      quantity: this.cartItem ? this.cartItem.quantity : undefined
    });

    document.dispatchEvent(new CustomEvent('gtmEvent:subscription_toggle', {
      detail: {
        is_selected: wantsSubscribe,
        selected_index: this.select ? this.select.selectedIndex : 0,
        variant_id: this.cartItem.variant_id, // The item key is now different, so look up by variant ID.
      },
    }));
  }

  async onChangePlan() {
    // Only meaningful if subscribed (checkbox on)
    if (!this.checkbox || !this.checkbox.checked) {
      return;
    }

    await this.pushChange({
      id: this.key,
      selling_plan: Number(this.select.value),
      quantity: this.cartItem ? this.cartItem.quantity : undefined
    });
  }

  async pushChange(changeData) {
    await fetch(`${Shopify.routes.root}cart/change.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(changeData)
    });

    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
      bubbles: true
    }))

    if (window.themeVariables.settings.pageType !== "cart") { return }
    window.location.reload()
  }
};
if (!window.customElements.get('line-item-selling-plan-selector')) {
  window.customElements.define('line-item-selling-plan-selector', LineItemSellingPlanSelector);
}

export {
  GiftWithPurchase
}

if (!window.customElements.get('custom-quick-buy')) {
  class CustomQuickBuy extends HTMLElement {
    constructor() {
      super();
      this.attachShadow({ mode: 'open' });
      this.shadowRoot.innerHTML = `
        <slot name="trigger"></slot>
        <slot name="sold-out"></slot>
        <slot name="trigger-mask"></slot>
        <slot name="variant-list"></slot>
      `;
      this.hasVariants = false;
      this.cartDrawer = null;
    }

    /**
     * Getter for the displayVariants property.
     * @returns {boolean} - True if the data attribute is true, false otherwise.
     */
    get displayVariants() {
      return this.dataset.displayVariants == 'true';
    }

    /**
     * Setter for the displayVariants property.
     * When set, it updates `data-display-variants` attribute on the host element.
     * @param {boolean} value - The new state for displaying variants.
     */
    set displayVariants(value) {
      if (!this.hasVariants) {
        return;
      }
      this.setAttribute('data-display-variants', value);
    }

    /**
     * Gets the selected variant property from the host's dataset.
     * @returns {string | boolean} The ID of the selected variant, or `false` if no variant is selected.
     */
    get selectedVariant() {
      return this.dataset.selectedVariant || false;
    }

    /**
     * Sets or clears the selected variant, but only updates if multiple variants are available.
     *
     * If a truthy `value` is provided, this sets the `data-selected-variant` attribute.
     * If a falsy value is passed, this finds and unchecks the currently selected
     * radio input and removes the data attribute.
     *
     * @param {string | null | undefined} value - The ID of the variant to set, or a falsy value to clear it.
     */
    set selectedVariant(value) {
      if (!this.hasVariants) {
        // Any default variant attribute value must persist.
        return;
      }

      if (value) {
        this.setAttribute('data-selected-variant', value);
        return;
      }

      if (this.selectedVariant) {
        const selectedInput = this.getSelectedInput();
        selectedInput.checked = false;
        this.removeAttribute('data-selected-variant');
      }
    }

    /**
     * Finds and returns the currently selected variant input element.
     * @returns {HTMLInputElement | null} The selected input element, or `null` if not found.
     */
    getSelectedInput = () => {
      return this.querySelector(`input[name="id"][value="${this.selectedVariant}"]`);
    }

    /**
     * Closes the variant list if a click occurs outside of this component.
     * @param {MouseEvent} event
     */
    handleOutslideClick = (event) => {
      if (!this.displayVariants) {
        return;
      }
      if (!event.composedPath().includes(this)) {
        this.displayVariants = false;
        this.selectedVariant = null;
      }
    }

    /**
     * Handles keydown events for the component.
     * @param {KeyboardEvent} event
     */
    handleKeyDown = (event) => {
      if (event.key !== 'Enter' || !this.displayVariants) {
        return;
      }
      // Check if the focused element is a label within this component.
      const focusedElement = document.activeElement;
      if (focusedElement && focusedElement.tagName === 'LABEL' && this.contains(focusedElement)) {
        event.preventDefault();

        // Find the radio input associated with the focused label.
        const inputId = focusedElement.getAttribute('for');
        if (!inputId) {
          return;
        }

        const input = this.querySelector(`#${inputId}`);
        if (!input) {
          return;
        }

        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
      }
    }

    /**
     * Adds one unit of the selected variant to the cart.
     */
    addToCart = () => {
      if (!this.selectedVariant) {
        console.error('Custom quick buy failed: No variant is selected.');
        return;
      }

      const { sellingPlanId } = this.getSelectedInput().dataset;

      const items = [
        {
          id: this.selectedVariant,
          quantity: 1,
          selling_plan: sellingPlanId || null,
        },
      ];

      this.setAttribute('aria-busy', true);

      fetch(`${Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ items }),
      })
      .then((response) => response.json())
      .then((response) => {
        // Trigger the cart to refresh itself. If the cart is openedf while this happens then
        // scroll to the variant when it's been added.
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
          detail: {
            lineItem: response?.items[0],
            scrollToItem: true,
          },
          bubbles: true
        }));
      })
      .catch(error => {
        this.dispatchEvent(new CustomEvent('cart:error', {
          bubbles: true,
          detail: {
            error: error.message,
          }
        }));
      })
      .finally(() => {
        this.setAttribute('aria-busy', false);

        if (this.cartDrawer) {
          this.cartDrawer.show();
        } else {
          window.location.href = Shopify.routes.root + 'cart';
        }
      });
    }

    connectedCallback() {
      // The theme's cart drawer.
      this.cartDrawer = document.getElementById('cart-drawer');

      // Dismiss on any outside click.
      document.addEventListener('click', this.handleOutslideClick);

      // Handle keyboard events.
      this.addEventListener('keydown', this.handleKeyDown);

      // Connect variant list.
      const variantListSlot = this.shadowRoot.querySelector('slot[name="variant-list"]');
      variantListSlot.addEventListener('slotchange', () => {
        this.hasVariants = true;
      });

      // Connect quick add button.
      const triggerSlot = this.shadowRoot.querySelector('slot[name="trigger"]');
      triggerSlot.addEventListener('slotchange', () => {
        const element = triggerSlot.assignedElements()[0];

        element.addEventListener('click', (e) => {
          e.preventDefault();

          if (this.selectedVariant) {
            this.addToCart();
            return;
          }

          this.displayVariants = !this.displayVariants;
        });
      });

      this.addEventListener('change', (e) => {
        if (e.target.name === 'id') {
          this.selectedVariant = e.target.value;
        }
      });
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.handleOutslideClick);
      this.removeEventListener('keydown', this.handleKeyDown);
    }
  }

  customElements.define('custom-quick-buy', CustomQuickBuy);
}
